import React, { useContext, useEffect } from 'react';

import { locale as localeConfig } from '@adsk/au-common-js';
import cn from 'classnames';

import { useBeehiveQuery } from '../../../apis/beehive/hooks';
import Card from '../../../components/presentational/card';
import CardGrid from '../../../components/presentational/card-grid/CardGrid';
import Skeleton from '../../../components/presentational/skeleton';
import languageOptions from '../../../helpers/languageOptions';
import { RelatedArticleCard } from '../../../templates/article/components';
import { ConfigContext } from '../../contexts/ConfigContext';

export interface MsgCard {
  idx: number;
  title: string;
  header: string;
  footer: string;
}

export interface Props {
  baseUrl?: string;
  langCode: string;
  max?: number;
  largeCardIdx?: number;
  msgCard?: MsgCard;
  taxonomyTermId?: string;
  industry?: string;
  topic?: string;
  product?: string;
}

function FeaturedArticles({
  langCode,
  max = 3,
  largeCardIdx = 1,
  msgCard,
  industry,
  topic,
  product,
  taxonomyTermId,
  baseUrl,
}: Props) {
  const { COMMUNITY_FUNCTIONS_URL } = useContext(ConfigContext);

  const parsedBaseUrl = baseUrl || COMMUNITY_FUNCTIONS_URL;

  const { data, isLoading } = useBeehiveQuery({
    baseURL: parsedBaseUrl,
    params: {
      locale: langCode,
      'fields.recordtype': 'article',
      'fields.contentusage': 'article',
      'fields.featured': true,
      ...(industry && { 'fields.industry': industry }),
      ...(topic && { 'fields.topic': topic }),
      ...(product && { 'fields.product': product }),
      pageSize: max,
      pageNum: 0,
      sort: 'created,desc',
    },
  });

  // TODO: Remove this useEffect once fully migrated to new filtering logic.
  useEffect(() => {
    if (taxonomyTermId) {
      // eslint-disable-next-line no-console
      console.info('deprecated "taxonomyTermId" is used', taxonomyTermId);
    }
  }, [taxonomyTermId]);

  const beehiveArticleCards = data?.items?.length ? [...data.items] : [];
  if (msgCard) {
    beehiveArticleCards.splice(msgCard.idx, 0, { ...msgCard, id: 'msg-card' });
  }

  const skeletons = Array.from({ length: max }).map((_, idx) => (
    <Skeleton
      /* eslint-disable-next-line react/no-array-index-key */
      key={`placeholder-${idx}`}
      width="100%"
      height={400}
      className={cn('auo-card', {
        'auo-card__w2x': idx === largeCardIdx,
      })}
    />
  ));

  return (
    <CardGrid className="au-w-full">
      {isLoading
        ? skeletons
        : beehiveArticleCards.map((item, idx) => {
            let card = null;
            if (msgCard && item.id === 'msg-card') {
              card = (
                <Card
                  key="msg-card"
                  title={msgCard.title}
                  header={msgCard.header}
                  footer={msgCard.footer}
                  innerPadding={false}
                  outline={false}
                  bookmark={false}
                  theme="black"
                />
              );
            } else {
              let header: string | undefined;
              if (item?.fields?.publishedDate) {
                const date = new Date(item.fields.publishedDate);
                const dateMonthDay = date.toLocaleDateString('en', {
                  month: 'long',
                  day: 'numeric',
                });
                header = `${dateMonthDay}, ${date.getFullYear()}`;
              }
              card = (
                <RelatedArticleCard
                  userProfileURL={parsedBaseUrl}
                  userContentURL={parsedBaseUrl}
                  articleData={item}
                  width={idx === largeCardIdx ? '2x' : '1x'}
                  language={langCode}
                  header={header}
                />
              );
            }
            return card;
          })}
    </CardGrid>
  );
}

// Shared attribute definition for Block Editor and AURR.
FeaturedArticles.attributes = {
  baseUrl: {
    type: 'string',
    default: 'https://api.au-uw2-prd.autodesk.com',
  },
  taxonomyTermId: {
    type: 'string',
    default: '',
  },
  industry: {
    type: 'string',
    default: '',
  },
  topic: {
    type: 'string',
    default: '',
  },
  product: {
    type: 'string',
    default: '',
  },
  langCode: {
    type: 'string',
    enum: localeConfig.urlLocales,
    default: localeConfig.defaultURLLocale,
    list: languageOptions,
  },
  max: {
    type: 'integer',
    default: 3,
  },
  largeCardIdx: {
    type: 'integer',
    default: 1,
  },
  displayMsgCard: {
    type: 'boolean',
    default: false,
  },
  msgCard: {
    type: 'object',
    default: {
      idx: 0,
      header: '',
      title: '',
      footer: '',
    },
  },
};

export default FeaturedArticles;
